import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  cookieConsentService,
  CookieConsent,
  CookieCategory,
} from "../services/cookieConsentService";
import "./CookieConsentBanner.css";

interface CookieConsentBannerProps {
  onConsentChange?: (consent: CookieConsent) => void;
}

const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({
  onConsentChange,
}) => {
  const { t, i18n } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [consent, setConsent] = useState<CookieConsent | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // קביעת כיוון הטקסט לפי השפה
  const direction = i18n.language === "he" ? "rtl" : "ltr";

  useEffect(() => {
    // טעינת הסכמה קיימת
    const existingConsent = cookieConsentService.getConsent();
    if (existingConsent) {
      setConsent(existingConsent);
    }

    // בדיקה אם צריך להציג את הבאנר
    const shouldShow = cookieConsentService.shouldShowBanner();
    setIsVisible(shouldShow);
  }, []);

  // פונקציה לטיפול בהסכמה מלאה
  const handleAcceptAll = async () => {
    setIsLoading(true);

    try {
      const newConsent: CookieConsent = {
        essential: true,
        functional: true,
        analytics: true,
        marketing: true,
        timestamp: Date.now(),
        version: "1.0.0",
      };

      cookieConsentService.setConsent(newConsent);
      setConsent(newConsent);
      setIsVisible(false);

      if (onConsentChange) {
        onConsentChange(newConsent);
      }

          } catch (error) {
          } finally {
      setIsLoading(false);
    }
  };

  // פונקציה לטיפול בדחייה מלאה
  const handleRejectAll = async () => {
    setIsLoading(true);

    try {
      const newConsent: CookieConsent = {
        essential: true, // תמיד true
        functional: false,
        analytics: false,
        marketing: false,
        timestamp: Date.now(),
        version: "1.0.0",
      };

      cookieConsentService.setConsent(newConsent);
      setConsent(newConsent);
      setIsVisible(false);

      // מחיקת קוקיז לא מורשים
      cookieConsentService.cleanupUnauthorizedCookies();

      if (onConsentChange) {
        onConsentChange(newConsent);
      }

      
      // הסתרת הבאנר אחרי דחייה
      setIsVisible(false);
    } catch (error) {
          } finally {
      setIsLoading(false);
    }
  };

  // פונקציה לטיפול בהתאמה אישית
  const handleCustomize = () => {
    setShowDetails(true);
  };

  // פונקציה לשמירת התאמה אישית
  const handleSaveCustomization = async (
    customConsent: Partial<CookieConsent>
  ) => {
    setIsLoading(true);

    try {
      const newConsent: CookieConsent = {
        essential: true, // תמיד true
        functional: customConsent.functional ?? false,
        analytics: customConsent.analytics ?? false,
        marketing: customConsent.marketing ?? false,
        timestamp: Date.now(),
        version: "1.0.0",
      };

      cookieConsentService.setConsent(newConsent);
      setConsent(newConsent);
      setIsVisible(false);
      setShowDetails(false);

      // מחיקת קוקיז לא מורשים
      cookieConsentService.cleanupUnauthorizedCookies();

      if (onConsentChange) {
        onConsentChange(newConsent);
      }

          } catch (error) {
          } finally {
      setIsLoading(false);
    }
  };

  // אם לא צריך להציג את הבאנר או שיש הסכמה תקפה
  if (!isVisible || (consent && !cookieConsentService.shouldShowBanner())) {
    return null;
  }

  return (
    <div className="cookie-consent-overlay" dir={direction}>
      <div className="cookie-consent-banner">
        {!showDetails ? (
          // תצוגה בסיסית
          <div className="cookie-banner-content">
            <div className="cookie-banner-header">
              <div className="cookie-icon">🍪</div>
              <h3 className="cookie-banner-title">{t("cookieBannerTitle")}</h3>
            </div>

            <p className="cookie-banner-description">
              {t("cookieBannerDescription")}
            </p>

            <div className="cookie-banner-actions">
              <button
                className="cookie-btn cookie-btn-accept"
                onClick={handleAcceptAll}
                disabled={isLoading}
              >
                {isLoading ? t("loading") : t("acceptAll")}
              </button>
              <button
                className="cookie-btn cookie-btn-customize"
                onClick={handleCustomize}
                disabled={isLoading}
              >
                {t("customize")}
              </button>

              <button
                className="cookie-btn cookie-btn-reject"
                onClick={handleRejectAll}
                disabled={isLoading}
              >
                {isLoading ? t("loading") : t("rejectAll")}
              </button>
            </div>

            <div className="cookie-banner-links">
              <a href="/privacy-policy" className="cookie-link">
                {t("privacyPolicy")}
              </a>
            </div>
          </div>
        ) : (
          // תצוגה מפורטת
          <CookieConsentDetails
            onSave={handleSaveCustomization}
            onCancel={() => setShowDetails(false)}
            isLoading={isLoading}
            currentConsent={consent}
          />
        )}
      </div>
    </div>
  );
};

// רכיב התאמה אישית מפורטת
interface CookieConsentDetailsProps {
  onSave: (consent: Partial<CookieConsent>) => void;
  onCancel: () => void;
  isLoading: boolean;
  currentConsent: CookieConsent | null;
}

const CookieConsentDetails: React.FC<CookieConsentDetailsProps> = ({
  onSave,
  onCancel,
  isLoading,
  currentConsent,
}) => {
  const { t } = useTranslation();
  const [customConsent, setCustomConsent] = useState<Partial<CookieConsent>>({
    functional: currentConsent?.functional ?? false,
    analytics: currentConsent?.analytics ?? false,
    marketing: currentConsent?.marketing ?? false,
  });

  const handleCategoryChange = (category: CookieCategory, enabled: boolean) => {
    setCustomConsent((prev) => ({
      ...prev,
      [category]: enabled,
    }));
  };

  const handleSave = () => {
    onSave(customConsent);
  };

  return (
    <div className="cookie-details-content">
      <div className="cookie-details-header">
        <h3 className="cookie-details-title">{t("cookieCustomizeTitle")}</h3>
        <p className="cookie-details-subtitle">
          {t("cookieCustomizeDescription")}
        </p>
      </div>

      <div className="cookie-categories">
        {/* קוקיז חיוניים - תמיד מופעלים */}
        <div className="cookie-category essential-category">
          <div className="category-header">
            <div className="category-info">
              <h4 className="category-title">{t("cookieEssentialTitle")}</h4>
              <p className="category-description">
                {t("cookieEssentialDescription")}
              </p>
            </div>
            <div className="category-toggle disabled">
              <input
                type="checkbox"
                checked={true}
                disabled={true}
                className="category-checkbox"
              />
              <span className="toggle-label">{t("alwaysActive")}</span>
            </div>
          </div>
        </div>

        {/* קוקיז פונקציונליים */}
        <div className="cookie-category">
          <div className="category-header">
            <div className="category-info">
              <h4 className="category-title">{t("cookieFunctionalTitle")}</h4>
              <p className="category-description">
                {t("cookieFunctionalDescription")}
              </p>
            </div>
            <div className="category-toggle">
              <input
                type="checkbox"
                checked={customConsent.functional ?? false}
                onChange={(e) =>
                  handleCategoryChange("functional", e.target.checked)
                }
                className="category-checkbox"
              />
              <span className="toggle-label">
                {customConsent.functional ? t("enabled") : t("disabled")}
              </span>
            </div>
          </div>
        </div>

        {/* קוקיז אנליטיים */}
        <div className="cookie-category">
          <div className="category-header">
            <div className="category-info">
              <h4 className="category-title">{t("cookieAnalyticsTitle")}</h4>
              <p className="category-description">
                {t("cookieAnalyticsDescription")}
              </p>
            </div>
            <div className="category-toggle">
              <input
                type="checkbox"
                checked={customConsent.analytics ?? false}
                onChange={(e) =>
                  handleCategoryChange("analytics", e.target.checked)
                }
                className="category-checkbox"
              />
              <span className="toggle-label">
                {customConsent.analytics ? t("enabled") : t("disabled")}
              </span>
            </div>
          </div>
        </div>

        {/* קוקיז שיווקיים */}
        <div className="cookie-category">
          <div className="category-header">
            <div className="category-info">
              <h4 className="category-title">{t("cookieMarketingTitle")}</h4>
              <p className="category-description">
                {t("cookieMarketingDescription")}
              </p>
            </div>
            <div className="category-toggle">
              <input
                type="checkbox"
                checked={customConsent.marketing ?? false}
                onChange={(e) =>
                  handleCategoryChange("marketing", e.target.checked)
                }
                className="category-checkbox"
              />
              <span className="toggle-label">
                {customConsent.marketing ? t("enabled") : t("disabled")}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="cookie-details-actions">
        <button
          className="cookie-btn cookie-btn-secondary"
          onClick={onCancel}
          disabled={isLoading}
        >
          {t("cancel")}
        </button>

        <button
          className="cookie-btn cookie-btn-accept"
          onClick={handleSave}
          disabled={isLoading}
        >
          {isLoading ? t("saving") : t("savePreferences")}
        </button>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
